// StatusGlyph: status is a small shape plus colour, never a filled badge, a tinted row or a coloured
// background. Drawn in SVG so it never depends on a font's glyphs. Colours are the contract's custom
// properties (src/styles/contract.css), so no hex lives in a view.
//
//   empty   Confirmed · Open · Not on file      (hollow circle, meta ink)
//   half    Checked in · Watch                  (half-filled circle, blue: progress, never grey)
//   full    In room · with patient              (filled blue circle)
//   done    Done · In target                    (green check)
//   clock   Due today                           (amber clock)
//   alert   Overdue · Critical · Needs attention (red circle with !)
//   up/down Above / below target                (amber triangle)
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { GLYPH_SHAPE, type GlyphKind } from './status-glyph-tokens';

export type { GlyphKind } from './status-glyph-tokens';

export function StatusGlyph({
  kind,
  size = 10,
  title,
}: {
  kind: GlyphKind;
  size?: number;
  title?: string;
}) {
  const common = {
    'data-glyph': kind,
    width: size,
    height: size,
    viewBox: '0 0 10 10',
    className: 'shrink-0',
    role: title ? 'img' : undefined,
    'aria-label': title,
    'aria-hidden': title ? undefined : true,
  } as const;
  switch (kind) {
    case 'empty':
      return (
        <svg {...common}>
          <circle cx="5" cy="5" r="4" fill="none" stroke={GLYPH_SHAPE.empty} strokeWidth="1.5" />
        </svg>
      );
    case 'half':
      return (
        <svg {...common}>
          <circle cx="5" cy="5" r="4.1" fill="none" stroke={GLYPH_SHAPE.half} strokeWidth="1.5" />
          <path d="M5 0.9 A4.1 4.1 0 0 1 5 9.1 Z" fill={GLYPH_SHAPE.half} />
        </svg>
      );
    case 'full':
      return (
        <svg {...common}>
          <circle cx="5" cy="5" r="4.75" fill={GLYPH_SHAPE.full} />
        </svg>
      );
    case 'done':
      return (
        <svg {...common}>
          <path
            d="M1.5 5.25 L4 7.75 L8.5 2.5"
            fill="none"
            stroke={GLYPH_SHAPE.done}
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'clock':
      return (
        <svg {...common}>
          <circle cx="5" cy="5" r="4.1" fill="none" stroke="var(--status-attention)" strokeWidth="1.5" />
          <path
            d="M5 2.6 V5 L6.7 6.1"
            fill="none"
            stroke="var(--status-attention)"
            strokeWidth="1.3"
            strokeLinecap="round"
          />
        </svg>
      );
    case 'alert':
      return (
        <svg {...common}>
          <circle cx="5" cy="5" r="5" fill="var(--status-bad)" />
          <rect x="4.35" y="2" width="1.3" height="3.9" rx="0.65" fill="var(--surface-read)" />
          <circle cx="5" cy="7.5" r="0.75" fill="var(--surface-read)" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <path
            d={kind === 'up' ? 'M5 1 L9.5 9 L0.5 9 Z' : 'M5 9 L9.5 1 L0.5 1 Z'}
            fill="var(--status-attention)"
          />
        </svg>
      );
  }
}

/** A glyph with its status written beside it in the matching colour (label role). Text colour comes from
 *  the `[data-glyph-text]` rules in showcase-bridge.css. */
export function GlyphText({
  kind,
  children,
  className,
  ...data
}: {
  kind: GlyphKind;
  children: ReactNode;
  className?: string;
} & Record<`data-${string}`, string | boolean | undefined>) {
  return (
    <span
      {...data}
      data-glyph-text={kind}
      className={cn('inline-flex items-center gap-2 whitespace-nowrap text-xs font-medium', className)}
    >
      <StatusGlyph kind={kind} />
      {children}
    </span>
  );
}
